import { Link } from 'react-router-dom';

const FEATURES = [
  {
    title: 'Real-Time Analytics',
    description:
      'GPU-accelerated data processing and AI inference for real-time insights and intelligent decision support.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: 'On-Premise Processing',
    description:
      'All data processing runs locally on the device -- no cloud dependency. Your data stays on your hardware with full operational security.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
  },
  {
    title: 'Secure by Design',
    description:
      'Built with security at every layer. Encrypted data channels ensure sensitive information remains under your control at all times.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    title: 'Multi-Source Integration',
    description:
      'Designed to integrate with multiple data sources and sensors into a unified analytics platform for comprehensive situational awareness.',
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
  },
];

export default function AIBox() {
  return (
    <div className="relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-3xl" />
        <div className="absolute bottom-1/3 -left-40 w-[400px] h-[400px] rounded-full bg-blue-500/8 blur-3xl" />
      </div>

      {/* ==================== HERO ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-sm text-cyan-400 font-medium">Flagship Product</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
              AI-BOX{' '}
              <span className="text-gradient">Edge Appliance</span>
            </h1>

            <p className="mt-6 text-lg text-slate-400 leading-relaxed">
              A ruggedized, GPU-accelerated on-premise AI appliance designed for secure data
              processing, real-time analytics, and intelligent decision support. The AIBOX
              delivers AI inference where operational security and reliability matter most.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="px-8 py-3 btn-gradient text-white font-semibold rounded-lg"
              >
                Request Pricing
              </Link>
              <Link
                to="/products"
                className="px-8 py-3 border border-navy-600 hover:border-cyan-500/50 text-slate-300 hover:text-white font-semibold rounded-lg transition-colors"
              >
                View All Products
              </Link>
            </div>
          </div>

          {/* Right - Product Image */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-2xl" />
            <div className="relative bg-navy-800/60 border border-navy-700 rounded-2xl p-4 overflow-hidden">
              <img
                src="/images/aibox.jpeg"
                alt="AIBOX Edge Appliance"
                className="w-full h-auto rounded-xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES GRID ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Core <span className="text-gradient">Capabilities</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            Every AIBOX ships with a comprehensive software stack ready for immediate deployment.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative bg-navy-800/60 backdrop-blur border border-navy-700 rounded-2xl p-8 hover:border-cyan-500/30 transition-all duration-300"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex gap-5">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400">
                  {f.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-sm">{f.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pb-28">
        <div className="relative bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-10 sm:p-16 text-center overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-cyan-500/10 blur-2xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-blue-500/10 blur-2xl" />

          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Interested in the AI-BOX?
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Contact our team for detailed specifications, integration support,
              and pricing information.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-block px-10 py-3 btn-gradient text-white font-semibold rounded-lg"
              >
                Contact for Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
